import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, AlertCircle, Loader2, Upload, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { QUOTE_STATUSES, formatINR, formatDate } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { StatusBadge, DivisionTag } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function NewQuoteDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { data: leadsData } = useQuery({
    queryKey: ["my-leads-for-quote"],
    queryFn: async () => (await api.get("/leads", { params: { page_size: 200 } })).data,
    enabled: open,
  });
  const { data: oppsData } = useQuery({
    queryKey: ["my-opps-for-quote"],
    queryFn: async () => (await api.get("/opportunities")).data,
    enabled: open,
  });
  const leads = (leadsData?.items || []).filter((l) => !l.converted_opportunity_id);
  const opps = (Array.isArray(oppsData) ? oppsData : []).filter((o) => o.status !== "lost");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Quotation</DialogTitle>
          <DialogDescription>Quote against one of your leads, or an opportunity in the pipeline.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">My Leads</Label>
            <Select value="" onValueChange={(v) => { onOpenChange(false); navigate(`/quotes/new?lead=${v}`); }}>
              <SelectTrigger className="h-9" data-testid="quote-lead-select"><SelectValue placeholder={leads.length ? "Pick a lead…" : "No open leads"} /></SelectTrigger>
              <SelectContent className="max-h-64">
                {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.project_name || l.name || l.company}{l.company ? ` · ${l.company}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400">Per sales policy, a lead becomes an Opportunity once its quotation is shared.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Opportunities</Label>
            <Select value="" onValueChange={(v) => { onOpenChange(false); navigate(`/quotes/new?opp=${v}`); }}>
              <SelectTrigger className="h-9" data-testid="quote-opp-select"><SelectValue placeholder={opps.length ? "Pick an opportunity…" : "No open opportunities"} /></SelectTrigger>
              <SelectContent className="max-h-64">
                {opps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.company ? ` · ${o.company}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Quotes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { divisionParam, isGlobal } = useDivision();
  const [status, setStatus] = useState("");
  const [dtpBusy, setDtpBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const isApprover = user?.role === "sales_head" || user?.role === "super_admin";
  const canCreate = !["factory", "management"].includes(user?.role);

  const importDtp = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setDtpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/erp-items/dtp-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`DTP prices updated on ${data.updated} items${data.not_matched ? ` · ${data.not_matched} rows didn't match any item code` : ""}`);
    } catch (err) { toast.error(apiError(err)); } finally { setDtpBusy(false); }
  };

  const pending = useQuery({
    queryKey: ["pending-approvals"],
    queryFn: async () => (await api.get("/quotations/pending-approvals")).data,
    enabled: isApprover,
  });

  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ["quotes", divisionParam, status],
    queryFn: async () => (await api.get("/quotations", { params: { division_id: divisionParam, status } })).data,
  });

  return (
    <div>
      <PageHeader title="Quotations / BOQ" subtitle="Build, route for Sales Head approval, and track quotes.">
        {user?.role === "super_admin" && (
          <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer" data-testid="dtp-import-btn" title="Upload a DTP price list (ItemCode + DTP columns) — prices auto-fill in the quote builder">
          {dtpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import DTP Prices
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importDtp} />
        </label>
        )}
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="quote-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
        {canCreate && (
          <Button onClick={() => setNewOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="new-quote-btn"><Plus className="h-4 w-4" /> New Quotation</Button>
        )}
      </PageHeader>

      {isApprover && pending.data?.length > 0 && (
        <Card className="p-5 mb-6 border-amber-200 bg-amber-50/50" data-testid="approval-queue">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <h3 className="font-heading font-semibold text-sm text-amber-900">{pending.data.length} quotation{pending.data.length > 1 ? "s" : ""} awaiting your approval</h3>
          </div>
          <div className="space-y-2">
            {pending.data.map((q) => (
              <button key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} className="w-full flex items-center justify-between p-3 rounded-md bg-white border border-amber-200 hover:border-amber-300 transition-colors text-left" data-testid={`pending-quote-${q.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{q.quote_number} · {q.deal_name}</p>
                    <p className="text-xs text-slate-500">by {q.created_by_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono text-sm font-semibold">{formatINR(q.total_amount)}</span>
                  {isGlobal && <DivisionTag id={q.division_id} />}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Quote #", "Deal", "Company", "Total", "Status", "Created", isGlobal && "Division"].filter(Boolean).map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {quotes.map((q) => (
                  <TableRow key={q.id} className="cursor-pointer" onClick={() => navigate(`/quotes/${q.id}`)} data-testid={`quote-row-${q.id}`}>
                    <TableCell className="font-mono font-medium">{q.quote_number} <span className="text-slate-400">v{q.version_number}</span></TableCell>
                    <TableCell className="text-slate-700">{q.deal_name}{q.lead_id && !q.opportunity_id ? <span className="ml-1.5 text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">Lead</span> : null}</TableCell>
                    <TableCell className="text-slate-600">{q.company}</TableCell>
                    <TableCell className="font-mono font-medium">{formatINR(q.total_amount)}</TableCell>
                    <TableCell><StatusBadge kind="quote" value={q.status} /></TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDate(q.created_at)}</TableCell>
                    {isGlobal && <TableCell><DivisionTag id={q.division_id} /></TableCell>}
                  </TableRow>
                ))}
                {quotes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">No quotations yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      <NewQuoteDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
